import { Button, Checkbox, ListItemText, Chip, DialogTitle, TextField, Dialog, Tooltip as MuiTooltip, IconButton, DialogContent } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { useSelector } from 'react-redux';
import React, { useState, useEffect, useRef } from "react";
import { selectMainTheme } from "app/store/fuse/settingsSlice";
import { ThemeProvider } from "@material-ui/core/styles";
import { TbWaveSine } from "react-icons/tb";
import { PiSteps } from "react-icons/pi";
import { useSipWebsocket } from 'app/protocol/SipWebsocketProvider';
import FuseLoading from '@fuse/core/FuseLoading';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import DeviceEditService from "../service/DeviceEditService";
import deviceControl from '../service/DeviceService';

const intervalButtons = [
    { label: 'All', unit: null },
    { label: '1 min', unit: { min: 1 } },
    { label: '2 mins', unit: { min: 2 } },
    { label: '3 mins', unit: { min: 3 } },
    { label: '4 mins', unit: { min: 4 } },
    { label: '5 mins', unit: { min: 5 } },
    { label: '10 mins', unit: { min: 10 } },
    { label: '20 mins', unit: { min: 20 } },
    { label: '30 mins', unit: { min: 30 } },
    { label: '60 mins', unit: { min: 60 } },
];

const CLASSIFICATION_PREFIX = 'classificationResult:';
const CLASSIFICATION_DASH_PATTERN = '1 5';

export default function StreamingPopup(props) {
    const { message } = useSipWebsocket();

    const [fullData, setFullData] = useState([]);
    const [debugMessages, setDebugMessages] = useState([]);
    const [debugOpen, setDebugOpen] = useState(false);
    const [debugCopied, setDebugCopied] = useState(false);

    const [selectedUnits, setSelectedUnits] = useState([]);
    const [streamingInProgress, setStreamingInProgress] = useState(false);
    const [uom, setUom] = useState(null);
    const [deviceData, setDeviceData] = useState({});
    const [selectedSensor, setSelectedSensor] = useState('');
    const [unitMapping, setUnitMapping] = useState([]);
    const [showAllInOneChart, setShowAllInOneChart] = useState(true);
    const [loadingData, setLoadingData] = useState(false);
    const [sensorName, setSensorName] = useState('');
    const [lineType, setLineType] = useState('monotone');
    const [selectedInterval, setSelectedInterval] = useState(intervalButtons[0]);
    const [highlightedKey, setHighlightedKey] = useState(null);
    const [legendHeight, setLegendHeight] = useState(36);
    const isMounted = useRef(true);

    const mainTheme = useSelector(selectMainTheme);

    const MIN_SEPARATION = 36;
    const COLOR_CAPACITY = Math.floor(360 / MIN_SEPARATION);
    const SAT = 80;
    const LIGHT = 45;

    const DASH_PATTERNS = [
        '0',         // solid
        '6 4',       // dash
        '4 4',       // short dash
        '3 6',       // dot
        '6 4 3 4',   // dash-dot
    ];

    const PALETTE = React.useMemo(
        () =>
            Array.from({ length: COLOR_CAPACITY }, (_, i) => {
                const hue = Math.round(((i + 0.5) * 360) / COLOR_CAPACITY) % 360;
                const sat = SAT + (((i * 7) % 3) - 1) * 4;
                const light = LIGHT + (((i * 11) % 3) - 1) * 4;
                return `hsl(${hue}, ${sat}%, ${light}%)`;
            }),
        []
    );

    const allSeriesKeys = React.useMemo(() => {
        const s = new Set();
        fullData.filter(d => d.deviceName === props.item.name).forEach(d => {
            s.add(`${d.unit}-${d.sid}-${d.vid}`);
            Object.keys(d.classificationResult || {}).forEach(key => s.add(`${CLASSIFICATION_PREFIX}${key}`));
        });

        unitMapping.forEach(m => {
            if (m.valueId) s.add(`${m.unit}-${m.sensorId}-${m.valueId}`);
            if (m.valueKey) s.add(`${m.unit}-${m.sensorId}-${m.valueKey}`);
        });

        return Array.from(s).sort((a, b) => a.localeCompare(b));
    }, [fullData, unitMapping, props.item?.name]);

    const stylesByKey = React.useMemo(() => {
        const m = new Map();
        allSeriesKeys.forEach((k, i) => {
            const color = PALETTE[i % COLOR_CAPACITY];
            const tier = Math.floor(i / COLOR_CAPACITY) % DASH_PATTERNS.length;
            m.set(k, { stroke: color, dash: DASH_PATTERNS[tier] });
        });
        return m;
    }, [allSeriesKeys, PALETTE]);

    const handleIntervalChange = (interval) => {
        setSelectedInterval(interval);
    };

    const handleToggleAllUnits = () => {
        if (selectedUnits.length === unitMapping.length) {
            setSelectedUnits([]);
        } else {
            setSelectedUnits([...unitMapping]);
        }
    };

    const getUnitKey = (unit) => `${unit.unit}-${unit.sensorId}-${unit.valueId}`;

    const getChartKeyForUnit = (unit) => {
        const keySuffix = `-${unit.sensorId}-${unit.valueId}`;
        return allSeriesKeys.find(key => !key.startsWith(CLASSIFICATION_PREFIX) && key.endsWith(keySuffix)) || getUnitKey(unit);
    };

    const IntervalButtons = () => (
        <div className='flex gap-4 items-center'>
            {intervalButtons.map(({ label, unit }) => (
                <MuiTooltip key={label} title={(unit ? 'Within ' : '') + label} arrow>
                    <span>
                        <IconButton
                            size='small'
                            onClick={() => handleIntervalChange({ label, unit })}
                            disabled={selectedInterval.label === label}
                            className={`rounded-full border ${selectedInterval.label === label
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'hover:bg-gray-100 border-gray-300'
                                }`}
                        >
                            <span className='text-xs font-semibold p-4'>{label}</span>
                        </IconButton>
                    </span>
                </MuiTooltip>
            ))}

            <MuiTooltip title='Toggle line type' arrow>
                <span>
                    <IconButton size='small' onClick={() => setLineType(prev => prev === 'monotone' ? 'step' : 'monotone')}>
                        {lineType === 'monotone' ? <PiSteps size={20} /> : <TbWaveSine size={20} />}
                    </IconButton>
                </span>
            </MuiTooltip>

            <MuiTooltip title={selectedUnits.length === unitMapping.length ? 'Deselect All' : 'Select All'} arrow>
                <span>
                    <IconButton
                        size='small'
                        onClick={handleToggleAllUnits}
                        disabled={unitMapping.length === 0}
                        className="rounded-full border hover:bg-gray-100 border-gray-300"
                    >
                        <span className='text-xs font-semibold p-4 whitespace-nowrap'>
                            {selectedUnits.length === unitMapping.length ? 'Deselect All' : 'Select All'}
                        </span>
                    </IconButton>
                </span>
            </MuiTooltip>
        </div>
    );

    const getDisplayData = () => {
        console.log('fullData sample:', fullData.slice(-5));
        if (!selectedInterval.unit || fullData.length === 0) return fullData.filter(d => d.deviceName === props.item.name);

        const latestTime = new Date(fullData[fullData.length - 1].time);
        const { min } = selectedInterval.unit;
        const earliestTime = new Date(latestTime.getTime() - min * 60 * 1000);

        fullData.slice(-5).forEach(d => {
            console.log('d.time:', d.time, 'parsed:', new Date(d.time));
        });

        return fullData.filter(d => {
            const dTime = new Date(d.time);
            return dTime >= earliestTime && dTime <= latestTime && d.deviceName === props.item.name;
        });
    };

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        onOpen();
    }, []);

    const stringifyRawMessage = (rawMessage) => {
        try {
            return JSON.stringify(rawMessage, null, 2);
        } catch (e) {
            return String(rawMessage);
        }
    };

    const copyDebugMessages = () => {
        const text = debugMessages.map(item => item.raw).join('\n\n');

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(() => {
                setDebugCopied(true);
                setTimeout(() => setDebugCopied(false), 1500);
            });
            return;
        }

        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setDebugCopied(true);
        setTimeout(() => setDebugCopied(false), 1500);
    };

    const formatClassificationLabel = (key) => key.replace(CLASSIFICATION_PREFIX, '');

    const formatTooltipValue = (value, name, tooltipProps) => {
        const dataKey = tooltipProps?.dataKey || name;

        if (String(dataKey).startsWith(CLASSIFICATION_PREFIX)) {
            return [`${Number(value).toFixed(2)} %`, formatClassificationLabel(String(dataKey))];
        }

        const unit = String(dataKey).split('-')[0];
        return [`${value} ${unit}`, name];
    };

    const renderTooltip = ({ active, payload, label }) => {
        if (!active || !payload || payload.length === 0) {
            return null;
        }

        return (
            <div
                style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.72)',
                    color: '#fff',
                    borderRadius: 4,
                    padding: '7px 9px',
                    fontSize: 11,
                    fontWeight: 600,
                    lineHeight: '16px',
                    maxWidth: 280,
                }}
            >
                <div style={{ marginBottom: 4 }}>{label}</div>
                {payload
                    .filter((item, index, arr) => arr.findIndex(entry => entry.dataKey === item.dataKey) === index)
                    .map(item => {
                    const [value, itemLabel] = formatTooltipValue(item.value, item.name, item);

                    return (
                        <div key={item.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: 8,
                                    backgroundColor: item.color,
                                    flex: '0 0 auto',
                                }}
                            />
                            <span>{itemLabel}: {value}</span>
                        </div>
                    );
                })}
            </div>
        );
    };

    useEffect(() => {
        if (message && streamingInProgress && message.type !== 'PONG' && message.payload?.deviceName === props.item.name) {
            setDebugMessages(prevMessages => [
                ...prevMessages,
                {
                    id: `${Date.now()}-${prevMessages.length}`,
                    receivedAt: new Date(),
                    raw: stringifyRawMessage(message),
                }
            ]);

            const formattedData = (message.payload.measurementData?.data || []).map(data => {
                const formatted = formatTime(data.time);
                console.log('Raw time:', data.time, 'Formatted:', formatted);
                return {
                    ...data,
                    time: formatted,
                    deviceName: message.payload.deviceName,
                    classificationResult: message.payload.classificationResult || null,
                };
            });

            setFullData(prevData => [...prevData, ...formattedData]);
        }
    }, [message, streamingInProgress]);

    useEffect(() => {
        if (streamingInProgress && uom) {
            console.log('Streaming in progress and UOM is set. Ready to display chart.');
        }
    }, [streamingInProgress, uom]);

    useEffect(() => {
        if (!deviceData.deviceType) return;

        const mapping = [];

        deviceData.deviceType.sensorTypeList.forEach(sensorType => {
            sensorType.valueList.forEach(value => {
                const sensorConfig = deviceData.sensorConfigs.find(
                    config => config.sensorExternalId === sensorType.externalId
                );
                const sensor = sensorConfig?.name || sensorType.name;

                mapping.push({
                    name: `${sensor}.${value.name}`,
                    unit: value.unit || 'N/A',
                    sensorId: sensorType.externalId,
                    valueId: value.externalId,
                    valueKey: value.name,
                });
            });
        });

        setUnitMapping(mapping);
    }, [deviceData]);


    useEffect(() => {
        if (unitMapping.length > 0 && selectedUnits.length === 0) {
            setSelectedUnits(unitMapping);
        }
    }, [unitMapping]);

    const formatTime = (timeString) => {
        const [date, time] = timeString.split(' ');
        const [yy, mm, dd] = date.split('/');
        const yyyy = Number(yy) < 100 ? `20${yy}` : yy;
        return `${yyyy}-${mm}-${dd} ${time.split('.')[0]}`;
    };

    const onOpen = () => {
        setFullData([]);
        setSelectedUnits([]);
        setSelectedSensor('');
        setUnitMapping([]);
        setShowAllInOneChart(true);
        setLoadingData(true);
        setSelectedInterval(intervalButtons[0]);
        setDebugMessages([]);
        setDebugOpen(false);
        setDebugCopied(false);
        setHighlightedKey(null);
        setLegendHeight(36);

        deviceControl.findById(props.item.id).then(data => {
            setDeviceData(data);

            const firstSensor = data.deviceType.sensorTypeList[0];
            if (firstSensor) {
                setSelectedSensor(firstSensor.externalId);
                setSensorName(firstSensor.name);
            }

            if (props.startStreaming) {
                DeviceEditService.startStreaming(props.item.id).then(resp => {
                    setStreamingInProgress(true);
                });
            }
            else {
                setStreamingInProgress(true);
            }
        });
    };

    const onClose = () => {
        props.onClose();
        DeviceEditService.stop(props.item.id).then(resp => {
            if (!isMounted.current) return;
            setSelectedInterval(intervalButtons[0]);
            setStreamingInProgress(false);
            setFullData([]);
            setSelectedUnits([]);
            setSelectedSensor('');
            setUnitMapping([]);
            setDebugMessages([]);
            setDebugOpen(false);
            setDebugCopied(false);
            setHighlightedKey(null);
        });
    };

    const renderLineChart = (unitObjs) => {
        const allData = getDisplayData();
        const chartKey = Array.isArray(unitObjs)
            ? unitObjs.map(u => `${u.sensorId}-${u.valueId}`).join('|') || 'all-data'
            : `${unitObjs.sensorId}-${unitObjs.valueId}`;

        const unitKeySet = new Set(
            (Array.isArray(unitObjs) ? unitObjs : [unitObjs])
                .map(u => `${u.sensorId}-${u.valueId}`)
        );

        const filteredData = allData.filter(d =>
            unitKeySet.has(`${d.sid}-${d.vid}`)
        );

        const sensorNameLookup = {};
        deviceData.deviceType.sensorTypeList.forEach(sensor => {
            const config = deviceData.sensorConfigs.find(
                (cfg) => cfg.sensorExternalId === sensor.externalId
            );
            sensorNameLookup[sensor.externalId] = config?.name || sensor.name;
        });

        const dataMap = {};
        filteredData.forEach(data => {
            if (!dataMap[data.time]) {
                dataMap[data.time] = {};
            }

            const sensorValueName = `${data.unit}-${data.sid}-${data.vid}`;
            dataMap[data.time][sensorValueName] = data.val;

            Object.entries(data.classificationResult || {}).forEach(([key, value]) => {
                dataMap[data.time][`${CLASSIFICATION_PREFIX}${key}`] = value * 100;
            });
        });

        const allKeys = new Set();
        const classificationKeys = new Set();

        filteredData.forEach(data => {
            const sensorValueName = `${data.unit}-${data.sid}-${data.vid}`;
            allKeys.add(sensorValueName);

            Object.keys(data.classificationResult || {}).forEach(key => {
                classificationKeys.add(`${CLASSIFICATION_PREFIX}${key}`);
            });
        });

        Object.keys(dataMap).forEach(time => {
            allKeys.forEach(key => {
                if (!Object.prototype.hasOwnProperty.call(dataMap[time], key)) {
                    dataMap[time][key] = null;
                }
            });
            classificationKeys.forEach(key => {
                if (!Object.prototype.hasOwnProperty.call(dataMap[time], key)) {
                    dataMap[time][key] = null;
                }
            });
        });

        const chartData = Object.keys(dataMap).map(time => ({
            time,
            ...dataMap[time],
        }));

        const valueTypes = Array.from(allKeys)
            .map(key => {
                const [unitName, sid, vid] = key.split('-');
                return { key, unitName, sid, vid, sensorName: sensorNameLookup[sid] || sid };
            })
            .sort((a, b) => {
                if (a.sensorName !== b.sensorName) return a.sensorName.localeCompare(b.sensorName);
                if (a.unitName !== b.unitName) return a.unitName.localeCompare(b.unitName);
                return a.vid.localeCompare(b.vid);
            })
            .map(x => x.key);

        const classificationValueTypes = Array.from(classificationKeys).sort((a, b) => a.localeCompare(b));
        const legendValueTypes = [...valueTypes, ...classificationValueTypes];

        const CustomizedDot = React.memo(({ cx, cy, payload }) => {
            if (payload.outlier) return <circle cx={cx} cy={cy} r={5} fill='red' stroke='white' strokeWidth={1.5} />;
            return null;
        });

        const HoverDot = React.memo(({ cx, cy }) => (
            <circle cx={cx} cy={cy} r={4} fill='#252f3e' stroke='white' strokeWidth={1.5} />
        ));

        return (
            <div key={chartKey} className="w-full flex-1" style={{ minHeight: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 10, right: 28, bottom: 16, left: 12 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="time" />
                        <YAxis
                            yAxisId="measurement"
                            domain={[0, dataMax => Math.max(10, dataMax * 1.1)]}
                            tickFormatter={(value) => Math.round(value)}
                        />
                        <YAxis
                            yAxisId="classification"
                            orientation="right"
                            domain={[0, 100]}
                            tickFormatter={(value) => `${Math.round(value)}%`}
                        />
                        <Tooltip content={renderTooltip} />
                        <Legend
                            verticalAlign="bottom"
                            align="center"
                            layout="horizontal"
                            wrapperStyle={{ maxWidth: '100%', whiteSpace: 'normal', lineHeight: '18px' }}
                            height={legendHeight}
                            content={() => (
                                <div
                                    ref={node => {
                                        if (!node) return;
                                        const height = Math.ceil(node.getBoundingClientRect().height);
                                        if (height && height !== legendHeight) {
                                            setLegendHeight(height);
                                        }
                                    }}
                                    style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', paddingTop: 8 }}
                                >
                                    {legendValueTypes.map((valueType) => {
                                        const st = stylesByKey.get(valueType) || { stroke: '#8884d8', dash: '0' };
                                        const isClassification = valueType.startsWith(CLASSIFICATION_PREFIX);
                                        const [unitName, sensorId] = valueType.split('-');
                                        const label = isClassification
                                            ? `${formatClassificationLabel(valueType)} (%)`
                                            : `${unitName} (${sensorNameLookup[sensorId] || sensorId})`;

                                        return (
                                            <div key={valueType} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <svg width="26" height="12" aria-hidden>
                                                    <line
                                                        x1="0"
                                                        y1="6"
                                                        x2="26"
                                                        y2="6"
                                                        stroke={st.stroke}
                                                        strokeWidth="3"
                                                        strokeDasharray={isClassification ? CLASSIFICATION_DASH_PATTERN : '0'}
                                                        strokeLinecap={isClassification ? 'round' : 'butt'}
                                                    />
                                                </svg>
                                                <span style={{ fontSize: 12 }}>{label}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        />
                        {valueTypes.map((valueType) => {
                            const st = stylesByKey.get(valueType) || { stroke: '#8884d8', dash: '0' };
                            const { stroke } = st;
                            const highlighted = highlightedKey === valueType;
                            const dimmed = highlightedKey && !highlighted;

                            return (
                                <Line
                                    key={valueType}
                                    yAxisId="measurement"
                                    type={lineType}
                                    dataKey={valueType}
                                    stroke={stroke}
                                    strokeDasharray="0"
                                    strokeOpacity={dimmed ? 0.25 : 1}
                                    strokeWidth={highlighted ? 4 : 2}
                                    name={`${valueType.split('-')[0]} (${sensorNameLookup[valueType.split('-')[1]] || valueType.split('-')[1]})`}
                                    dot={<CustomizedDot />}
                                    activeDot={<HoverDot />}
                                    isAnimationActive={false}
                                    connectNulls
                                />
                            );
                        })}
                        {classificationValueTypes.map((valueType) => {
                            const st = stylesByKey.get(valueType) || { stroke: '#111827', dash: '8 3' };
                            const dimmed = Boolean(highlightedKey);

                            return (
                                <Line
                                    key={valueType}
                                    yAxisId="classification"
                                    type={lineType}
                                    dataKey={valueType}
                                    stroke={st.stroke}
                                    strokeDasharray={CLASSIFICATION_DASH_PATTERN}
                                    strokeLinecap="round"
                                    strokeOpacity={dimmed ? 0.25 : 1}
                                    strokeWidth={2}
                                    name={formatClassificationLabel(valueType)}
                                    dot={false}
                                    activeDot={<HoverDot />}
                                    isAnimationActive={false}
                                    connectNulls
                                />
                            );
                        })}
                        {highlightedKey && valueTypes.includes(highlightedKey) && (() => {
                            const st = stylesByKey.get(highlightedKey) || { stroke: '#8884d8' };

                            return (
                                <Line
                                    key={`${highlightedKey}-highlight`}
                                    yAxisId="measurement"
                                    type={lineType}
                                    dataKey={highlightedKey}
                                    stroke={st.stroke}
                                    strokeDasharray="0"
                                    strokeWidth={5}
                                    name={highlightedKey}
                                    dot={false}
                                    activeDot={<HoverDot />}
                                    isAnimationActive={false}
                                    connectNulls
                                    legendType="none"
                                />
                            );
                        })()}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    };

    useEffect(() => {
        if (fullData.length > 0) {
            console.log('Checking data availability for selected sensor:', selectedSensor);
            console.log('FullData latest:', fullData.slice(-5));

            const sameDeviceData = fullData.some(data => data.deviceName === props.item.name);
            if (sameDeviceData) {
                console.log('Data found for current device. Turning off loading.');
                setLoadingData(false);
            }
        }
    }, [fullData, selectedSensor]);

    return (
        <ThemeProvider theme={mainTheme}>
            <Dialog
                open
                fullWidth
                maxWidth={fullData.length || debugOpen ? "lg" : "sm"}
                onClose={onClose}
                PaperProps={{ style: { height: fullData.length ? '90vh' : 'auto', minHeight: 560, minWidth: 960 } }}
            >
                <DialogTitle disableTypography className="border-b flex flex-col px-0">
                    <div className={`flex items-center justify-between gap-32 px-32 ${fullData.length ? 'border-b pb-14' : ''}`}>
                        <div className="whitespace-nowrap">
                            {props.title ?? `Timeseries${props.name ? ` data for ${props.name}` : ''}`}
                        </div>
                        <div className="flex items-center gap-8">
                            <Button
                                variant={debugOpen ? 'contained' : 'outlined'}
                                color={debugOpen ? 'secondary' : 'default'}
                                onClick={() => setDebugOpen(prev => !prev)}
                            >
                                Debug ({debugMessages.length})
                            </Button>
                            <Button onClick={onClose}>
                                Close
                            </Button>
                        </div>
                    </div>
                    <div className="w-full flex flex-col gap-8 shadow-inner px-32 pt-14">
                        <div className="w-full flex flex-col gap-8 justify-between rounded-xl bg-gray-100 py-10 shadow px-16">
                            <div className="flex justify-between items-center gap-8">
                                <Autocomplete
                                    className="flex-1"
                                    multiple
                                    disableCloseOnSelect
                                    options={unitMapping}
                                    getOptionLabel={(option) => `${option.name} (${option.sensorId})`}
                                    value={selectedUnits}
                                    onChange={(e, newValue) => setSelectedUnits(newValue)}
                                    renderOption={(option, { selected }) => (
                                        <>
                                            <Checkbox checked={selected} style={{ marginRight: 8 }} />
                                            <ListItemText primary={option.name} />
                                        </>
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => {
                                            const tagProps = getTagProps({ index });
                                            const unitKey = getChartKeyForUnit(option);

                                            return (
                                                <Chip
                                                    key={unitKey}
                                                    label={option.name}
                                                    {...tagProps}
                                                    size="small"
                                                    onMouseEnter={() => setHighlightedKey(unitKey)}
                                                    onMouseLeave={() => setHighlightedKey(null)}
                                                    style={{
                                                        marginRight: 4,
                                                        marginBottom: 4,
                                                        height: 22,
                                                        fontSize: 11,
                                                        maxWidth: 190,
                                                    }}
                                                />
                                            );
                                        })
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            label="Filter units"
                                            placeholder="Select units to display"
                                        />
                                    )}
                                />
                            </div>

                            <IntervalButtons />
                        </div>
                    </div>
                </DialogTitle>
                <DialogContent className="w-full flex flex-col p-20" style={{ minHeight: 0, overflow: 'hidden' }}>
                    {debugOpen && (
                        <div className="w-full mb-20 border rounded p-16 bg-gray-50">
                            <div className="flex items-center justify-between gap-16 mb-12">
                                <div className="font-semibold">
                                    Raw websocket messages
                                </div>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={debugMessages.length === 0}
                                    onClick={copyDebugMessages}
                                >
                                    {debugCopied ? 'Copied' : 'Copy raw'}
                                </Button>
                            </div>

                            {debugMessages.length === 0 ? (
                                <div className="text-center text-gray-600 py-12">No debug messages yet</div>
                            ) : (
                                <div className="flex flex-col gap-8" style={{ maxHeight: 260, overflow: 'auto' }}>
                                    {debugMessages.map((item, index) => (
                                        <div key={item.id} className="border rounded bg-white p-10">
                                            <div className="text-xs text-gray-600 mb-6">
                                                #{index + 1} - {item.receivedAt.toLocaleString()}
                                            </div>
                                            <pre className="m-0 text-xs whitespace-pre-wrap break-words">{item.raw}</pre>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {loadingData ? (
                        <div className="w-full flex flex-1" style={{ minHeight: 360 }}>
                            <FuseLoading />
                        </div>
                    ) : getDisplayData().length === 0 ? (
                        <div className='text-center'>No data available</div>
                    ) : (
                        <div className="w-full flex-1 flex flex-col" style={{ minHeight: 400 }}>
                            {showAllInOneChart
                                ? renderLineChart(selectedUnits)
                                : selectedUnits.map(unitObj => renderLineChart(unitObj))
                            }
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </ThemeProvider>
    );
}
